import { Link } from "@tanstack/react-router";
import { ArrowRight, RotateCw } from "lucide-react";
import { useEffect } from "react";
import { AnimatedLogoMark } from "#/components/logo";
import { UI_STRINGS, useI18n } from "#/lib/i18n";

/** Router-level `defaultErrorComponent` — catches any error thrown while
 * rendering, loading, or in a `beforeLoad`, anywhere in the route tree,
 * instead of leaving the page blank or showing a raw stack trace. */
export function ErrorPage({
	error,
	reset,
}: {
	error: unknown;
	reset: () => void;
}) {
	const { locale, dir } = useI18n();
	const t = UI_STRINGS[locale].error;

	// Errors caught here would otherwise vanish with no trace — always log so
	// they show up in the browser console / server logs.
	useEffect(() => {
		console.error(error);
	}, [error]);

	return (
		<div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-canvas px-6 py-20 text-center">
			<AnimatedLogoMark mode="enter" className="h-20 w-auto" />

			<div className="flex flex-col gap-3">
				<span className="mx-auto inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-bold tracking-wide text-red-600">
					{t.eyebrow}
				</span>
				<h1 className="font-display text-3xl font-bold text-navy sm:text-4xl">
					{t.title}
				</h1>
				<p className="mx-auto max-w-md text-sm text-muted sm:text-base">
					{t.description}
				</p>
			</div>

			{import.meta.env.DEV ? (
				<pre
					dir="ltr"
					className="mx-auto max-w-xl overflow-x-auto rounded-xl border border-line bg-white p-4 text-start text-xs text-red-700"
				>
					{error instanceof Error
						? (error.stack ?? error.message)
						: String(error)}
				</pre>
			) : null}

			<div className="flex flex-wrap items-center justify-center gap-3">
				<button
					type="button"
					onClick={reset}
					className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-6 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-canvas"
				>
					<RotateCw className="h-4 w-4" />
					{t.retry}
				</button>
				<Link
					to="/"
					className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-bold text-white shadow-lift transition-all hover:-translate-y-0.5 hover:bg-navy-600 active:translate-y-0"
				>
					{t.cta}
					<ArrowRight
						className={`h-4 w-4 ${dir === "rtl" ? "rotate-180" : ""}`}
					/>
				</Link>
			</div>
		</div>
	);
}
